import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ShopOutlined,
  TagOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);
import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  createDish,
  deleteDish,
  editDish,
  fetchByCode,
  fetchTimes,
  updateFlashSale,
} from "./slice";
import flashSaleApi from "./api";
import { priceToCurrency, getPhotoUrl } from "utils/common";
import DefaultDishImg from "assets/img/no-avatar.jpg";

function FlashSale() {
  const dispatch = useDispatch();
  const {
    times,
    isLoading,
    currentSlotData,
    isLoadingSlot,
    dishes,
    isLoadingDishes,
  } = useSelector((state) => state.flashSale);
  const [day, setDay] = useState(dayjs());
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingDish, setEditingDish] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [form] = Form.useForm();
  const [selectedDates, setSelectedDates] = useState([]);
  const [datePickerValue, setDatePickerValue] = useState(null);

  // Flash Sale Register states
  const [registers, setRegisters] = useState([]);
  const [isLoadingRegisters, setIsLoadingRegisters] = useState(false);
  const [isDeletingRegister, setIsDeletingRegister] = useState(false);
  const [registerPagination, setRegisterPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filterProcessed, setFilterProcessed] = useState(null);

  // Cho phép chọn quá khứ và 15 ngày tiếp theo
  const disabledDate = (current) => {
    const end = dayjs().add(15, "day").endOf("day");
    return current && current > end;
  };

  // Kiểm tra day có nằm trong khoảng hôm nay -> 14 ngày sau
  const isDayInRange = () => {
    const today = dayjs().startOf("day");
    const maxDate = dayjs().add(15, "day").endOf("day");
    return (
      day.isAfter(today.subtract(1, "day")) &&
      day.isBefore(maxDate.add(1, "day"))
    );
  };
  const onDayChange = (date) => {
    setDay(date);
  };

  const onTabChange = (key) => {
    setActiveKey(key);
    const selectedTime = times.find((time) => time.code === key);
    if (selectedTime) {
      const code = `${selectedTime.code}${day.format("DDMMYY")}`;
      dispatch(fetchByCode(code));
    }
  };

  useEffect(() => {
    if (!times || times.length === 0) {
      dispatch(fetchTimes());
    }
  }, []);

  useEffect(() => {
    if (times && times.length > 0) {
      const firstTimeCode = times[0].code;
      setActiveKey(firstTimeCode);
      dispatch(fetchByCode(`${firstTimeCode}${day.format("DDMMYY")}`));
    }
  }, [times, day]);

  // Update status every minute for real-time display
  useEffect(() => {
    const interval = setInterval(() => {
      // Force re-render by updating a dummy state
      setActiveKey((prev) => prev);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Fetch flash sale registers
  const fetchRegisters = useCallback(async () => {
    setIsLoadingRegisters(true);
    try {
      const params = {
        current: registerPagination.current,
        pageSize: registerPagination.pageSize,
      };
      if (filterProcessed !== null) {
        params.processed = filterProcessed;
      }
      const response = await flashSaleApi.getRegisters(params);
      if (response.data) {
        setRegisters(response.data || []);
        if (response.meta) {
          setRegisterPagination((prev) => ({
            ...prev,
            current: response.meta.current || prev.current,
            pageSize: response.meta.pageSize || prev.pageSize,
            total: response.meta.total || 0,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching registers:", error);
    } finally {
      setIsLoadingRegisters(false);
    }
  }, [
    registerPagination.current,
    registerPagination.pageSize,
    filterProcessed,
  ]);

  useEffect(() => {
    fetchRegisters();
  }, [fetchRegisters]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  const handleAddDish = () => {
    setEditingDish(null);
    // Mặc định chọn ngày hiện tại (day)
    const defaultDate = day.format("DD-MM-YYYY");
    setSelectedDates([defaultDate]);
    setDatePickerValue(null);
    form.resetFields();
    form.setFieldValue("dates", [defaultDate]);
    setIsModalVisible(true);
  };

  const handleEditDish = (dish) => {
    setEditingDish(dish);
    form.setFieldsValue({
      dish: dish.dish._id,
      maxQuantity: dish.maxQuantity,
      minOrderValue: dish.minOrderValue,
      isPriority: dish.isPriority,
      active: dish.active,
    });
    setIsModalVisible(true);
  };

  const handleDeleteDish = async (dishId) => {
    const selectedTime = times.find((time) => time.code === activeKey);
    const currentCode = selectedTime
      ? `${selectedTime.code}${day.format("DDMMYY")}`
      : null;
    await dispatch(deleteDish(currentSlotData._id, dishId, currentCode));
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const selectedTime = times.find((time) => time.code === activeKey);
      const currentCode = selectedTime
        ? `${selectedTime.code}${day.format("DDMMYY")}`
        : null;

      // Format dates if provided (chỉ khi thêm mới, không phải sửa)
      if (!editingDish) {
        if (
          values.dates &&
          Array.isArray(values.dates) &&
          values.dates.length > 0
        ) {
          values.dates = values.dates.join(",");
        } else if (selectedDates && selectedDates.length > 0) {
          values.dates = selectedDates.join(",");
        }
      } else {
        // Khi sửa món, không gửi field dates
        delete values.dates;
      }

      if (editingDish) {
        await dispatch(
          editDish(currentSlotData._id, editingDish._id, values, currentCode)
        );
      } else {
        await dispatch(createDish(currentSlotData._id, values, currentCode));
      }

      // Close modal and reset form on success
      setIsModalVisible(false);
      form.resetFields();
      setEditingDish(null);
    } catch (error) {
      // Form validation error - keep modal open
      console.log("Form validation error:", error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingDish(null);
    setSelectedDates([]);
    setDatePickerValue(null);
  };

  const handleAddDate = (date) => {
    if (date) {
      const dateStr = dayjs(date).format("DD-MM-YYYY");
      if (!selectedDates.includes(dateStr)) {
        const newDates = [...selectedDates, dateStr];
        setSelectedDates(newDates);
        form.setFieldValue("dates", newDates);
        setDatePickerValue(null); // Reset DatePicker
      } else {
        toast.warning("Ngày này đã được chọn");
        setDatePickerValue(null); // Reset DatePicker
      }
    }
  };

  const handleRemoveDate = (dateToRemove) => {
    const newDates = selectedDates.filter((date) => date !== dateToRemove);
    setSelectedDates(newDates);
    form.setFieldValue("dates", newDates);
  };

  const handleUpdateFlashSale = async (updateData) => {
    await dispatch(updateFlashSale(currentSlotData._id, updateData));
  };

  const handleToggleProcessed = async (registerId, currentProcessed) => {
    try {
      await flashSaleApi.updateProcessed(registerId, !currentProcessed);
      // Refresh the list
      fetchRegisters();
    } catch (error) {
      console.error("Error updating processed status:", error);
    }
  };

  const handleDeleteRegister = async (registerId) => {
    try {
      setIsDeletingRegister(true);
      const response = await flashSaleApi.deleteRegister(registerId);
      if (response.success) {
        toast.success("Xóa thành công");
        // Refresh the list
        fetchRegisters();
      } else {
        toast.error(response.error?.msg || "Lỗi khi xóa đăng ký flashsale");
      }
    } catch (error) {
      console.error("Error deleting register:", error);
      const errorMessage =
        error.response?.data?.error?.msg ||
        error.response?.data?.message ||
        "Lỗi khi xóa đăng ký flashsale";
      toast.error(errorMessage);
    } finally {
      setIsDeletingRegister(false);
    }
  };

  const handleCopyDishId = async (dishId) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(dishId);
        message.success("Đã sao chép ID món ăn!");
      } else {
        // Fallback for browsers that don't support clipboard API
        const textArea = document.createElement("textarea");
        textArea.value = dishId;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        message.success("Đã sao chép ID món ăn!");
      }
    } catch (error) {
      console.error("Failed to copy:", error);
      message.error("Không thể sao chép ID món ăn!");
    }
  };

  const dishColumns = [
    {
      title: "Món ăn",
      dataIndex: "dish",
      key: "dishName",
      render: (dish) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Image
            alt="Dish image"
            src={
              dish?.image
                ? getPhotoUrl(dish.image?.path || null)
                : DefaultDishImg
            }
            width={50}
            height={50}
            style={{ borderRadius: "6px", flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: "bold" }}>{dish?.name || "N/A"}</div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              <ShopOutlined /> {dish?.restaurantName || "N/A"}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Giá bán",
      render: (record) =>
        record.dish.isDiscount ? (
          <>
            <small style={{ color: "#ff6c00" }}>
              <TagOutlined />{" "}
              {record.dish.discountType === "direct"
                ? `${
                    record.dish.discountValue % 1000 === 0
                      ? `${Math.floor(record.dish.discountValue / 1000)}K`
                      : `${record.dish.discountValue}₫`
                  }`
                : `${record.dish.discountValue}%`}
            </small>
            &nbsp;<del>{priceToCurrency(record.dish.rawPrice || 0)}</del>
            <br />
            <span>{priceToCurrency(record.dish.price)}</span>
          </>
        ) : (
          <span>{priceToCurrency(record.dish.price)}</span>
        ),
    },
    {
      title: "Đã bán",
      dataIndex: "count",
      key: "count",
      render: (count) => count || 0,
    },
    {
      title: "SL bán tối đa",
      dataIndex: "maxQuantity",
      key: "maxQuantity",
      render: (maxQuantity) => maxQuantity || 0,
    },
    {
      title: "GTĐH tối thiểu",
      dataIndex: "minOrderValue",
      key: "minOrderValue",
      render: (value) => formatCurrency(value || 0),
    },
    {
      title: "Slot ưu tiên",
      dataIndex: "isPriority",
      key: "isPriority",
      render: (isPriority) => (
        <Tag color={isPriority ? "geekblue" : "default"}>
          {isPriority ? "Ưu tiên" : "Không"}
        </Tag>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "active",
      key: "active",
      render: (active) =>
        active ? (
          <Tag color="#4caf50">Active</Tag>
        ) : (
          <Tag color="#f44336">In-active</Tag>
        ),
    },
    {
      title: "Thao tác",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditDish(record)}
          >
            Sửa
          </Button>
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa món ăn này?"
            onConfirm={() => handleDeleteDish(record._id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button danger size="small" icon={<DeleteOutlined />}>
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const registerColumns = [
    {
      title: "Món đăng ký",
      key: "dishRestaurant",
      render: (record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Image
            alt="Dish image"
            src={
              record.dish?.image
                ? getPhotoUrl(record.dish.image?.path || null)
                : DefaultDishImg
            }
            width={50}
            height={50}
            style={{ borderRadius: "6px", flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
              {record.dish?.name || "N/A"}
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleCopyDishId(record.dish?._id)}
                style={{ padding: 0, marginLeft: "8px" }}
              />
            </div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              <ShopOutlined /> {record.restaurant?.name || "N/A"}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Khung giờ",
      dataIndex: "time",
      key: "time",
    },
    {
      title: "Ngày đăng ký",
      dataIndex: "dates",
      key: "dates",
      width: 300,
      render: (dates) => (
        <div style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
          {dates?.split(",").join(", ") || "N/A"}
        </div>
      ),
    },
    {
      title: "SL tối đa",
      dataIndex: "maxQuantity",
      key: "maxQuantity",
      render: (maxQuantity) => maxQuantity || 0,
    },
    {
      title: "GTĐH tối thiểu",
      dataIndex: "minOrderValue",
      key: "minOrderValue",
      render: (value) => formatCurrency(value || 0),
    },
    {
      title: "Slot ưu tiên",
      dataIndex: "isPriority",
      key: "isPriority",
      render: (isPriority) => (
        <Tag color={isPriority ? "geekblue" : "default"}>
          {isPriority ? "Có" : "Không"}
        </Tag>
      ),
    },
    {
      title: "Tổng tiền",
      dataIndex: "total",
      key: "total",
      render: (total) => formatCurrency(total || 0),
    },
    {
      title: "Đã xử lý",
      dataIndex: "processed",
      key: "processed",
      render: (processed, record) => (
        <Switch
          checked={processed}
          onChange={() => handleToggleProcessed(record._id, processed)}
        />
      ),
    },
    {
      title: "Thời gian ĐK",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) =>
        createdAt ? dayjs(createdAt).format("DD-MM-YYYY HH:mm") : "N/A",
    },
    {
      title: "Thao tác",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title="Xác nhận xóa"
          description="Bạn có chắc chắn muốn xóa đăng ký flashsale này?"
          onConfirm={() => handleDeleteRegister(record._id)}
          okText="Xóa"
          cancelText="Hủy"
          okButtonProps={{ danger: true, loading: isDeletingRegister }}
        >
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            size="small"
            loading={isDeletingRegister}
          >
            Xóa
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const tabItems = times.map((slot) => ({
    key: slot.code,
    label: `${slot.fromText} - ${slot.toText}`,
    size: "large",
    children: (
      <Card>
        <Row gutter={[16, 16]}>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {currentSlotData && (
              <Row gutter={16}>
                <Col span={6}>
                  <Card size="small">
                    <Statistic
                      title="Trạng thái"
                      value={currentSlotData.status}
                      valueStyle={{
                        color: currentSlotData.statusColor,
                      }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small">
                    <Row justify="space-between" align="middle">
                      <Statistic
                        title="Kích hoạt"
                        value={
                          currentSlotData.active
                            ? "Đang kích hoạt"
                            : "Chưa kích hoạt"
                        }
                        valueStyle={{
                          color: currentSlotData.active ? "#3f8600" : "#cf1322",
                        }}
                      />
                      <Switch
                        checked={currentSlotData.active}
                        onChange={(checked) =>
                          handleUpdateFlashSale({ active: checked })
                        }
                        style={{ marginTop: 4 }}
                      />
                    </Row>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small">
                    <Row justify="space-between" align="middle">
                      <Statistic
                        title="Món ăn"
                        value={dishes.length}
                        valueStyle={{ color: "#1890ff" }}
                      />
                      {isDayInRange() && (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={handleAddDish}
                        >
                          Thêm món
                        </Button>
                      )}
                    </Row>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small">
                    <Statistic
                      title="Đã bán"
                      value={dishes.reduce(
                        (sum, dish) => sum + (dish.count || 0),
                        0
                      )}
                      valueStyle={{ color: "#722ed1" }}
                    />
                  </Card>
                </Col>
              </Row>
            )}
            {/* <Row justify="space-between" align="middle">
              <Typography.Title level={4}>
                &nbsp;
              </Typography.Title>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddDish}
                disabled={!currentSlotData}
              >
                Thêm món ăn
              </Button>
            </Row> */}
            {currentSlotData ? (
              <Spin spinning={isLoadingSlot || isLoadingDishes}>
                <Table
                  columns={dishColumns}
                  dataSource={dishes}
                  rowKey="_id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                  }}
                  locale={{
                    emptyText: "Chưa có món ăn nào trong khung giờ này",
                  }}
                />
              </Spin>
            ) : (
              <Empty description="Không có dữ liệu!" />
            )}
          </Space>
        </Row>
      </Card>
    ),
  }));

  return (
    <>
      <Row justify="space-between" align="middle">
        <Typography.Title level={2}>
          <ThunderboltOutlined /> Flash Sale
        </Typography.Title>
        <Space size="middle">
          <DatePicker
            value={day}
            onChange={onDayChange}
            format={"DD-MM-YYYY"}
            disabledDate={disabledDate}
          />
        </Space>
      </Row>
      <Divider />

      <Spin spinning={isLoading}>
        <Tabs
          activeKey={activeKey}
          items={tabItems}
          size="large"
          tabPosition="top"
          onChange={onTabChange}
        />
      </Spin>

      <Divider />

      <Card>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <Typography.Title level={4}>
            Danh sách đăng ký Flash Sale
          </Typography.Title>
          <Space>
            <span>Lọc theo trạng thái:</span>
            <Select
              value={filterProcessed}
              onChange={setFilterProcessed}
              style={{ width: 150 }}
              placeholder="Tất cả"
              allowClear
            >
              <Select.Option value={null}>Tất cả</Select.Option>
              <Select.Option value={true}>Đã xử lý</Select.Option>
              <Select.Option value={false}>Chưa xử lý</Select.Option>
            </Select>
          </Space>
        </Row>
        <Spin spinning={isLoadingRegisters}>
          <Table
            columns={registerColumns}
            dataSource={registers}
            rowKey="_id"
            pagination={{
              current: registerPagination.current,
              pageSize: registerPagination.pageSize,
              total: registerPagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `Tổng ${total} bản ghi`,
              onChange: (page, pageSize) => {
                setRegisterPagination({
                  ...registerPagination,
                  current: page,
                  pageSize: pageSize || registerPagination.pageSize,
                });
              },
            }}
            locale={{
              emptyText: "Chưa có đăng ký nào",
            }}
          />
        </Spin>
      </Card>

      <Modal
        title={editingDish ? "Sửa món ăn" : "Thêm món ăn"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            isPriority: false,
            active: true,
          }}
        >
          {editingDish ? (
            <Form.Item label="Món ăn hiện tại">
              <div
                style={{
                  padding: "8px 12px",
                  background: "#f5f5f5",
                  borderRadius: "6px",
                }}
              >
                <div style={{ fontWeight: "bold" }}>
                  {editingDish.dish?.name}
                </div>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  ID: {editingDish.dish?._id}
                </div>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  <ShopOutlined /> {editingDish.dish?.restaurantName || "N/A"}
                </div>
              </div>
            </Form.Item>
          ) : (
            <>
              <Form.Item
                name="dish"
                label="ID Món ăn"
                rules={[{ required: true, message: "Vui lòng nhập ID món ăn" }]}
              >
                <Input placeholder="Nhập ID món ăn" />
              </Form.Item>
              {!editingDish && (
                <Form.Item
                  name="dates"
                  label="Chọn ngày (tối đa 15 ngày)"
                  rules={[
                    {
                      required: true,
                      validator: () => {
                        if (selectedDates.length === 0) {
                          return Promise.reject(
                            new Error("Vui lòng chọn ít nhất một ngày")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "8px",
                        marginTop: 8,
                      }}
                    >
                      {Array.from({ length: `15` }, (_, i) => {
                        const date = dayjs().add(i, "day");
                        const dateStr = date.format("DD-MM-YYYY");
                        const isSelected = selectedDates.includes(dateStr);
                        const dayNames = [
                          "CN",
                          "T2",
                          "T3",
                          "T4",
                          "T5",
                          "T6",
                          "T7",
                        ];
                        const dayName = dayNames[date.day()];

                        return (
                          <Button
                            key={i}
                            type={isSelected ? "primary" : "default"}
                            onClick={() => {
                              if (isSelected) {
                                handleRemoveDate(dateStr);
                              } else {
                                if (selectedDates.length >= 15) {
                                  toast.warning("Chỉ được chọn tối đa 15 ngày");
                                  return;
                                }
                                handleAddDate(date);
                              }
                            }}
                            style={{
                              height: "auto",
                              padding: "8px 4px",
                              borderRadius: "6px",
                              border: isSelected ? "none" : "1px solid #d9d9d9",
                              backgroundColor: isSelected
                                ? "#ffd700"
                                : "#f5f5f5",
                              color: isSelected ? "#000" : "#000",
                              fontWeight: isSelected ? "bold" : "normal",
                            }}
                          >
                            <div
                              style={{ fontSize: "12px", lineHeight: "1.2" }}
                            >
                              <div>{dayName}</div>
                              <div>{date.format("DD/MM")}</div>
                            </div>
                          </Button>
                        );
                      })}
                    </div>
                    {selectedDates.length > 0 && (
                      <div
                        style={{ marginTop: 12, fontSize: 12, color: "#666" }}
                      >
                        Đã chọn: {selectedDates.length}/15 ngày
                      </div>
                    )}
                  </div>
                </Form.Item>
              )}
            </>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="maxQuantity" label="Số lượng bán tối đa">
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  placeholder="Nhập số lượng bán tối đa trong khung giờ này"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="minOrderValue"
                label="Giá trị đơn hàng tối thiểu (VNĐ)"
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  placeholder="Nhập giá trị đơn hàng tối thiểu"
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="isPriority"
                label="Hiển thị thứ tự ưu tiên"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="active"
                label="Kích hoạt"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default FlashSale;
